import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Navbar,
  NavbarBrand,
  Nav as ReactstrapNav,
  NavItem,
  NavLink,
  Button,
  Offcanvas,
  OffcanvasHeader, 
  OffcanvasBody,
} from "reactstrap";
import logo from "../assets/logo/main-logo.png";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaBars } from "react-icons/fa"; // For Hamburger Icon
import "../common.scss";
import { useDispatch, useSelector } from "react-redux";
import { logOut, setTrustData } from "../redux/slices/authSlice";
import { useQuery } from "@tanstack/react-query";
import { getTrustDetail, loginPage } from "../utility/commonApi";
import { fetchImage } from "../utility/commonDownloadS3Image";

const Nav = () => {
  const subdomainChange = process.env.REACT_APP_ADMIN_SUBDOMAIN_REPLACE_URL;
  const adminUrl = process.env.REACT_APP_ADMIN_URL;
  const hostname = window.location.host;
  const trustId = localStorage.getItem("trustId");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loggedOut, setLoggedOut] = useState(false);
  // const [user, setUser] = useState(null);
  const location = useLocation(); // Get the current route
  const userDetails = useSelector((state) => state.auth);
  const user = userDetails ? userDetails.user : null;
  const trustData = userDetails && userDetails?.trustData;
  const idols = trustData?.idols || [];
  const donationViaPortfolioApp = userDetails && userDetails?.trustData?.donationViaPortfolioApp;
  const hasDharmshala = userDetails && userDetails?.trustData?.hasDharmshala;
  const isApplicationEnabled =
    userDetails && userDetails?.trustData?.isApplicationEnabled;
  const isServicesEnabled =
    userDetails && userDetails?.trustData?.isSevaEnabled;

  const canLogin =
    isServicesEnabled ||
    hasDharmshala ||
    donationViaPortfolioApp ||
    isApplicationEnabled;

  const handleLogout = () => {
    const redirectUrl = localStorage.getItem("logoutRedirectUrl");
    dispatch(logOut());
    
    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      window.location.href = "/";
    }
  };

  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768); // Check if it's mobile
  const [activeLink, setActiveLink] = useState(location.pathname); // Track active link

  useEffect(() => {
    setActiveLink(location.pathname);
  }, [location]);
  useEffect(() => {
    const hash = location.hash; // Extract hash from the URL
    if (hash) {
      const element = document.querySelector(hash); // Select the target element
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location]);
  // Toggle Sidebar
  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const isActive = (path) => activeLink === path;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsOpen(false); // Close the sidebar on desktop
      }
    };

    window.addEventListener("resize", handleResize);

    // Cleanup event listener on component unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle active link change
  useEffect(() => {
    setActiveLink(location.pathname);
  }, [location]);

  let subDomainName;
  if (hostname !== adminUrl) {
    subDomainName = hostname.replace(subdomainChange, "");
  } else {
    subDomainName = hostname.replace(
      process.env.REACT_APP_GENERIC_ADMIN_SUBDOMAIN_REPLACE_URL,
      ""
    );
  }
  const tId = trustId ?? trustData?.id;
  const loginPageQuery = useQuery({
    queryKey: [tId],
    queryFn: () => getTrustDetail(tId),
  });

  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    if (trustData) {
      const loadImages = async () => {
        const url = await fetchImage(
          trustData.trustLogo ? `${trustData.trustLogo["name"]}` : null
        );
        setImageUrl(url);
      };

      loadImages();
    }
  }, [trustData]);
  useEffect(() => {
    if (loginPageQuery?.data) {
      dispatch(setTrustData(loginPageQuery.data.data.result));
    }
  }, [loginPageQuery, dispatch]);

  const isChaturmasMode = localStorage.getItem("logoutRedirectUrl")?.includes("bangalore-chaturmas");

  return (
    <div>
      <Navbar>
        <Container className="d-flex justify-content-between position-relative align-items-center">
          {/* Logo */}
          <NavbarBrand href={user ? "/home" : "/"}>
            <img
              alt="logo"
              src={
                ((imageUrl && imageUrl) ||
                  (trustData && trustData?.trustLogo?.presignedUrl)) ??
                logo
              }
              style={{
                height: 60,
                objectFit: "contain",
                width: 170,
                objectPosition: "left",
              }}
            />
          </NavbarBrand>

          {/* Centered Welcome Text */}
          {isChaturmasMode && trustData?.name && (
            <div
              className="position-absolute start-50 top-50 translate-middle text-center gold"
              style={{ fontSize: "clamp(1rem, 4vw, 2rem)", whiteSpace: "nowrap", zIndex: 0, marginTop: "5px" }}
            >
              Welcome to {trustData.name}
            </div>
          )}


          {/* Normal Menu Hidden on Mobile */}
          {!user && !isChaturmasMode && (
            <ReactstrapNav className="d-none d-md-flex align-items-center">
              <NavItem>
                <NavLink
                  tag={Link}
                  to="/"
                  className={isActive("/") ? "active" : ""}
                >
                  Home
                </NavLink>
              </NavItem>
              {(idols || []).some((idol) => idol.isMain) && (
                <NavItem>
                  <NavLink
                    tag={Link}
                    to="/idol"
                    className={isActive("/idol") ? "active" : ""}
                  >
                    <span className="idol-name">
                      {idols.find((idol) => idol.isMain)?.name || "Idol"}
                    </span>{" "}
                  </NavLink>
                </NavItem>
              )}

              <NavItem>
                <NavLink
                  tag={Link}
                  to="/about"
                  className={isActive("/about") ? "active" : ""}
                >
                  About Us
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  tag={Link}
                  to="/donation"
                  className={isActive("/donation") ? "active" : ""}
                >
                  Donation
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  tag={Link}
                  to="/updates"
                  className={isActive("/updates") ? "active" : ""}
                >
                  Updates
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  tag={Link}
                  to="/events"
                  className={isActive("/events") ? "active" : ""}
                >
                  Events
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  tag={Link}
                  to="/notices"
                  className={isActive("/notices") ? "active" : ""}
                >
                  Notices
                </NavLink>
              </NavItem>
              {trustData?.facilities?.length > 0 && (
                <NavItem>
                  <NavLink
                    tag={Link}
                    to="/#facilities"
                    className={isActive("/#facilities") ? "active" : ""}
                  >
                    Facilities
                  </NavLink>
                </NavItem>
              )}
              <NavItem>
                <NavLink
                  tag={Link}
                  to="/galleries"
                  className={isActive("/galleries") ? "active" : ""}
                >
                  Gallery
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  tag={Link}
                  to="/punyarjak"
                  className={isActive("/punyarjak") ? "active" : ""}
                >
                  Punyarjak
                </NavLink>
              </NavItem>
              {isServicesEnabled && (
                <NavItem>
                  <NavLink
                    tag={Link}
                    to="/seva"
                    className={isActive("/seva") ? "active" : ""}
                    state={{ from: "/seva" }}
                  >
                    Seva
                  </NavLink>
                </NavItem>
              )}
              {/* <NavItem>
                <NavLink
                  tag={Link}
                  to="/applications"
                  className={isActive("/applications") ? "active" : ""}
                  state={{ from: "/applications" }}
                >
                  Applications
                </NavLink>
              </NavItem> */}
              {/* <NavItem>
              <NavLink
                tag={Link}
                to="/seva"
                className={isActive("/seva") ? "active" : ""}
              >
                Seva
              </NavLink>
            </NavItem> */}
            </ReactstrapNav>
          )}

          {/* Donate Now Button */}
          <div className="d-flex">
            {/* {isSevaEnabled !== "false" && (
              <div className="d-none d-md-flex align-items-center me-1">
                <Button
                  color="primary"
                  tag={Link}
                  to="/login"
                  state={{ from: "/seva" }}
                >
                  Seva
                </Button>
              </div>
            )} */}
            {/* {!donationViaPortfolioApp && (
              <div className="d-none d-md-flex align-items-center me-1">
                <Button
                  color="primary"
                  tag={Link}
                  to="/login"
                  state={{ from: "/" }}
                >
                  Donate Now
                </Button>
              </div>
            )} */}
            <div className="d-none d-md-flex align-items-center">
              {user ? (
                <Button color="primary" onClick={handleLogout}>
                  Logout
                </Button>
              ) : (
                canLogin && (
                  <Button
                    color="primary"
                    tag={Link}
                    to="/login"
                    state={{ from: "/home" }}
                  >
                    Login
                  </Button>
                )
              )}
            </div>
          </div>

          {/* Hamburger Menu Visible on Mobile */}
          <div className="d-md-none d-flex align-items-center justify-content-center">
            <FaBars
              size={24}
              style={{ cursor: "pointer" }}
              onClick={toggleSidebar}
            />
          </div>
        </Container>
      </Navbar>

      {/* Sidebar Drawer Menu */}
      <Offcanvas
        isOpen={isOpen}
        toggle={toggleSidebar}
        direction="end"
        style={{ width: "335px" }}
        className="d-md-none"
      >
        <OffcanvasHeader toggle={toggleSidebar}>
          <img
            alt="logo"
            src={
              ((imageUrl && imageUrl) ||
                (trustData && trustData?.trustLogo?.presignedUrl)) ??
              logo
            }
            style={{
              height: 50,
              objectFit: "contain",
              width: 150,
            }}
          />
        </OffcanvasHeader>
        <OffcanvasBody>
          {!user && !isChaturmasMode && (
            <ReactstrapNav className="flex-column">
              {/* Menu Items */}
              <NavItem>
                <NavLink
                  tag={Link}
                  to="/"
                  onClick={() => {
                    setActiveLink("/");
                    toggleSidebar();
                  }}
                  className={`menu-item ${isActive("/") ? "active" : ""}`}
                >
                  Home
                </NavLink>
              </NavItem>
              {(idols || []).some((idol) => idol.isMain) && (
                <NavItem>
                  <NavLink
                    tag={Link}
                    to="/idol"
                    onClick={() => {
                      setActiveLink("/idol");
                      toggleSidebar();
                    }}
                    className={`menu-item ${isActive("/idol") ? "active" : ""}`}
                  >
                    <span className="idol-mobileView-name">
                      {idols.find((idol) => idol.isMain)?.name || "Idol"}
                    </span>
                  </NavLink>
                </NavItem>
              )}

              <NavItem>
                <NavLink
                  tag={Link}
                  to="/about"
                  onClick={() => {
                    setActiveLink("/about");
                    toggleSidebar();
                  }}
                  className={`menu-item ${isActive("/about") ? "active" : ""}`}
                >
                  About Us
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  tag={Link}
                  to="/donation"
                  onClick={() => {
                    setActiveLink("/donation");
                    toggleSidebar();
                  }}
                  className={`menu-item ${
                    isActive("/donation") ? "active" : ""
                  }`}
                >
                  Donation
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  tag={Link}
                  to="/updates"
                  onClick={() => {
                    setActiveLink("/updates");
                    toggleSidebar();
                  }}
                  className={`menu-item ${
                    isActive("/updates") ? "active" : ""
                  }`}
                >
                  Updates
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  tag={Link}
                  to="/events"
                  onClick={() => {
                    setActiveLink("/events");
                    toggleSidebar();
                  }}
                  className={`menu-item ${isActive("/events") ? "active" : ""}`}
                >
                  Events
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  tag={Link}
                  to="/notices"
                  onClick={() => {
                    setActiveLink("/notices");
                    toggleSidebar();
                  }}
                  className={`menu-item ${
                    isActive("/notices") ? "active" : ""
                  }`}
                >
                  Notices
                </NavLink>
              </NavItem>
              {trustData?.facilities?.length > 0 && (
                <NavItem>
                  <NavLink
                    tag={Link}
                    to="/#facilities"
                    onClick={() => {
                      setActiveLink("/#facilities");
                      toggleSidebar();
                    }}
                    className={`menu-item ${
                      isActive("/#facilities") ? "active" : ""
                    }`}
                  >
                    Facilities
                  </NavLink>
                </NavItem>
              )}
              <NavItem>
                <NavLink
                  tag={Link}
                  to="/galleries"
                  onClick={() => {
                    setActiveLink("/galleries");
                    toggleSidebar();
                  }}
                  className={`menu-item ${
                    isActive("/galleries") ? "active" : ""
                  }`}
                >
                  Gallery
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  tag={Link}
                  to="/punyarjak"
                  onClick={() => {
                    setActiveLink("/punyarjak");
                    toggleSidebar();
                  }}
                  className={`menu-item ${
                    isActive("/punyarjak") ? "active" : ""
                  }`}
                >
                  Punyarjak
                </NavLink>
              </NavItem>
              {isServicesEnabled && (
                <NavItem>
                  <NavLink
                    tag={Link}
                    to="/seva"
                    onClick={() => {
                      setActiveLink("/seva");
                      toggleSidebar();
                    }}
                    className={`menu-item ${isActive("/seva") ? "active" : ""}`}
                  >
                    Seva
                  </NavLink>
                </NavItem>
              )}
              {/* <NavItem>
              <NavLink
                tag={Link}
                to="/seva"
                onClick={() => {
                  setActiveLink("/seva");
                  toggleSidebar();
                }}
                className={`menu-item ${isActive("/seva") ? "active" : ""}`}
              >
                Seva
              </NavLink>
            </NavItem> */}
            </ReactstrapNav>
          )}
          {/* {isSevaEnabled !== "false" && (
            <div className="mt-3">
              <Button
                color="primary"
                tag={Link}
                to="/login"
                state={{ from: "/seva" }}
                block
              >
                Seva
              </Button>
            </div>
          )} */}
          {/* {!donationViaPortfolioApp && (
            <div className="mt-2">
              <Button
                color="primary"
                tag={Link}
                to="/login"
                state={{ from: "/" }}
                block
              >
                Donate Now
              </Button>
            </div>
          )} */}
          <div className="mt-2">
            {user ? (
              <Button color="primary" onClick={handleLogout} block>
                Logout
              </Button>
            ) : (
              canLogin && (
                <Button
                  color="primary"
                  tag={Link}
                  to="/login"
                  state={{ from: "/home" }}
                  block
                >
                  Login
                </Button>
              )
            )}
          </div>
        </OffcanvasBody>
      </Offcanvas>
    </div>
  );
};

export default Nav;
